/**
 * User Manager
 * 
 * Provides focused user management with consistent patterns
 */

import type { User, UserRoleType, UserStatusType, Organization, UserAccountSettings } from '@/lib/types';
import { authService } from '@/lib/firebase/auth';
// import { organizationManager } from './organizationManager'; // Not used in current implementation
import { isSampleDataMode } from '@/lib/config/env';
import { format } from 'date-fns';
import { FilterBuilder } from '@/lib/utils/apiHelpers';
// import { generateNumericId } from '@/lib/utils'; // Not used in current implementation
import { 
  getUserOrganizationMapping, 
  isSampleDataUser, 
  getUsersForOrganization as getSampleUsersForOrg 
} from '@/lib/sample-data/userOrganizationMapping';
// Sample data is now imported dynamically to avoid build issues in production

interface CreateUserData extends Omit<User, 'uid' | 'creationTime' | 'dateUpdated' | 'displayName'> {
  password?: string;
}

interface UserFilters {
  role?: UserRoleType;
  status?: UserStatusType;
  organizationId?: string;
  search?: string;
}

/**
 * UserManager - Manages user operations with Firebase Auth and sample data support
 */
class UserManager {
  private demoUsers: User[] = [];
  private userSettingsStore: { [userId: string]: UserAccountSettings } = {};

  private defaultSettingsForNewUser: Omit<UserAccountSettings, 'userUid'> = {
    notifications: {
      bookingConfirmations: true,
      bookingReminders: true,
      promotions: false,
    },
    appearance: {
      language: 'en',
    },
    display: {
      defaultView: 'day',
    },
  };

  constructor() {
    this.initializeData().catch(error => {
      console.error('Failed to initialize user data:', error);
      this.demoUsers = [];
      this.userSettingsStore = {};
    });
  }

  private async initializeData(): Promise<void> {
    try {
      // Safely handle sample data that might be undefined in production
      let sampleUsersData: User[] | undefined;
      let sampleSettingsData: { [userId: string]: UserAccountSettings } | undefined;
      
      try {
        // Only import sample data if we're in sample data mode
        if (isSampleDataMode()) {
          console.log('🔧 [UserManager] Loading sample data...');
          const sampleDataModule = await import('@/lib/sample-data');
          sampleUsersData = sampleDataModule.sampleUsers;
          sampleSettingsData = sampleDataModule.sampleAccountSettingsStore;
          
          console.log('📊 [UserManager] Sample data loaded:', {
            usersCount: sampleUsersData?.length || 0,
            settingsCount: Object.keys(sampleSettingsData || {}).length
          });
        } else {
          console.log('🔧 [UserManager] Sample data mode disabled');
        }
      } catch (importError) {
        console.error('⚠️ [UserManager] Sample data import failed:', importError);
        // Try alternative import strategy
        try {
          if (isSampleDataMode()) {
            console.log('🔄 [UserManager] Trying alternative import...');
            const { sampleUsers } = await import('@/lib/sample-data/users');
            const { sampleAccountSettingsStore } = await import('@/lib/sample-data/settings');
            sampleUsersData = sampleUsers;
            sampleSettingsData = sampleAccountSettingsStore;
            
            console.log('✅ [UserManager] Alternative import successful:', {
              usersCount: sampleUsersData?.length || 0,
              settingsCount: Object.keys(sampleSettingsData || {}).length
            });
          }
        } catch (alternativeError) {
          console.error('❌ [UserManager] Alternative import also failed:', alternativeError);
        }
      }
      
      this.demoUsers = sampleUsersData ? JSON.parse(JSON.stringify(sampleUsersData)) : [];
      this.userSettingsStore = sampleSettingsData ? JSON.parse(JSON.stringify(sampleSettingsData)) : {};
      
      console.log('✅ [UserManager] Initialization complete:', {
        demoUsersCount: this.demoUsers.length,
        settingsStoreCount: Object.keys(this.userSettingsStore).length
      });
    } catch (error) {
      console.error('❌ [UserManager] Failed to initialize user data:', error);
      this.demoUsers = [];
      this.userSettingsStore = {};
    }
  }

  /**
   * Add artificial delay for development
   */
  private async withDelay<T>(operation: () => Promise<T>): Promise<T> {
    // Simple delay for development
    await new Promise(resolve => setTimeout(resolve, 100));
    return operation();
  }

  /**
   * Get all users - Firebase Auth for authentication, sample data for demo content
   */
  async getAllUsers(): Promise<User[]> {
    return this.withDelay(async () => {
      if (isSampleDataMode()) {
        console.log('🔍 [UserManager] getAllUsers - Sample data mode enabled');
        
        if (this.demoUsers.length === 0) {
          console.log('🔄 [UserManager] Demo users not loaded, initializing...');
          await this.initializeData();
        }
        
        console.log('📊 [UserManager] Returning demo users:', { count: this.demoUsers.length });
        
        // Return sample users for demo content
        return JSON.parse(JSON.stringify(this.demoUsers));
      }

      console.log('🔍 [UserManager] getAllUsers - Production mode, fetching from Firebase');
      try {
        const firebaseUsers = await authService.getAllUsers();
        console.log('📊 [UserManager] Firebase users retrieved:', firebaseUsers.length);
        return firebaseUsers;
      } catch (error) {
        console.error('❌ [UserManager] Error fetching Firebase users:', error);
        return [];
      }
    });
  }

  /**
   * Get filtered users
   */
  async getUsers(filters: UserFilters = {}): Promise<User[]> {
    return this.withDelay(async () => {
      const allUsers = await this.getAllUsers();
      
      return new FilterBuilder(allUsers)
        .where('status', filters.status)
        .where('organizationId', filters.organizationId)
        .custom((user: User) => !filters.role || (user.roles?.includes(filters.role) ?? false))
        .search(filters.search, ['displayName', 'email', 'firstName', 'lastName'])
        .build();
    });
  }

  /**
   * Get users by role
   */
  async getUsersByRole(role: UserRoleType): Promise<User[]> {
    return this.withDelay(async () => {
      const allUsers = await this.getAllUsers();
      
      return new FilterBuilder(allUsers)
        .custom((user: User) => user.roles?.includes(role) ?? false)
        .build();
    });
  }

  /**
   * Get user by ID - always use Firebase Auth for authentication
   */
  async getUserById(userId: string): Promise<User | null> {
    return this.withDelay(async () => {
      if (isSampleDataMode()) {
        // Ensure data is initialized before lookup
        if (this.demoUsers.length === 0) {
          await this.initializeData();
        }
        
        const user = this.demoUsers.find(u => u.uid === userId);
        return user ? JSON.parse(JSON.stringify(user)) : null;
      }

      try {
        return await authService.getUserById(userId);
      } catch (error) {
        console.error('Error fetching Firebase user:', error);
        return null;
      }
    });
  }

  /**
   * Get user by email - always use Firebase Auth for authentication
   */
  async getUserByEmail(email: string): Promise<User | null> {
    return this.withDelay(async () => {
      if (isSampleDataMode()) {
        const user = this.demoUsers.find(u => u.email.toLowerCase() === email.toLowerCase());
        return user ? JSON.parse(JSON.stringify(user)) : null;
      }

      try {
        return await authService.getUserByEmail(email);
      } catch (error) {
        console.error('Error fetching Firebase user by email:', error);
        return null;
      }
    });
  }

  /**
   * Create user - always use Firebase Auth for authentication
   */
  async createUser(userData: CreateUserData): Promise<User | null> {
    return this.withDelay(async () => {
      // const newUser: User = { // Not used in current implementation
      //   ...userData,
      //   uid: '', // Will be set by Firebase
      //   displayName: this.generateDisplayName(userData),
      //   creationTime: format(new Date(), 'yyyy-MM-dd'),
      //   dateUpdated: format(new Date(), 'yyyy-MM-dd'),
      // };

      try {
        const createdUser = await authService.createUser({
          email: userData.email,
          password: userData.password || '',
          firstName: userData.firstName,
          lastName: userData.lastName,
          roles: userData.roles,
          organizationId: userData.organizationId,
          tenantId: userData.tenantId,
          phoneNumber: userData.phoneNumber,
          photoURL: userData.photoURL,
        });
        
        // Initialize default settings for new user
        if (createdUser) {
          this.userSettingsStore[createdUser.uid] = {
            ...this.defaultSettingsForNewUser,
            userUid: createdUser.uid,
          };
        }
        
        return createdUser;
      } catch (error) {
        console.error('Error creating Firebase user:', error);
        return null;
      }
    });
  }

  /**
   * Update user - always use Firebase Auth for authentication
   */
  async updateUser(userId: string, updates: Partial<User>): Promise<User | null> {
    return this.withDelay(async () => {
      const existingUser = await this.getUserById(userId);
      if (!existingUser) return null;

      const updatedUser = {
        ...existingUser,
        ...updates,
        displayName: this.generateDisplayName(updates, existingUser),
        dateUpdated: format(new Date(), 'yyyy-MM-dd'),
      };

      if (isSampleDataMode()) {
        return this.updateDemoUser(userId, updatedUser);
      }

      try {
        return await authService.updateUser(userId, updatedUser);
      } catch (error) {
        console.error('Error updating Firebase user:', error);
        return null;
      }
    });
  }

  /**
   * Delete user - always use Firebase Auth for authentication
   */
  async deleteUser(userId: string): Promise<boolean> {
    return this.withDelay(async () => {
      if (isSampleDataMode()) {
        const index = this.demoUsers.findIndex(user => user.uid === userId);
        if (index !== -1) {
          this.demoUsers.splice(index, 1);
          return true;
        }
        return false;
      }

      try {
        await authService.deleteUser(userId);
        return true;
      } catch (error) {
        console.error('Error deleting Firebase user:', error);
        return false;
      }
    });
  }

  /**
   * Get users for organization - sample data for demo content
   */
  async getUsersForOrganization(organizationId: string): Promise<User[]> {
    return this.withDelay(async () => {
      if (isSampleDataMode()) {
        const userMappings = getSampleUsersForOrg(organizationId);
        
        // Convert mappings to User objects
        return userMappings.map((mapping: any) => ({
          uid: mapping.uid,
          email: mapping.email,
          emailVerified: true,
          displayName: mapping.displayName,
          photoURL: '/user.png',
          firstName: mapping.firstName,
          lastName: mapping.lastName,
          roles: mapping.roles,
          status: 'Active' as UserStatusType,
          creationTime: new Date().toISOString(),
          lastSignInTime: new Date().toISOString(),
          organizationId: mapping.organizationId,
          tenantId: mapping.tenantId,
          associatedClubIds: mapping.associatedClubIds || [],
          providerData: [],
          customClaims: {
            roles: mapping.roles,
            organizationId: mapping.organizationId,
            tenantId: mapping.tenantId,
            firstName: mapping.firstName,
            lastName: mapping.lastName
          }
        }));
      }
      
      // In production, get all users and filter by organization
      const allUsers = await this.getAllUsers();
      return new FilterBuilder(allUsers)
        .where('organizationId', organizationId)
        .build();
    });
  }

  /**
   * Get the organization for a user (for sample data mode)
   */
  async getUserOrganization(user: User): Promise<Organization | null> {
    return this.withDelay(async () => {
      if (!isSampleDataMode() || !isSampleDataUser(user.uid)) {
        return null;
      }

      const mapping = getUserOrganizationMapping(user.uid);
      if (!mapping) {
        return null;
      }

      try {
        // Dynamically import sample data
        const sampleDataModule = await import('@/lib/sample-data/organizations');
        
        // Find the organization in sample data
        const organization = sampleDataModule.sampleOrganisationsDataForSuperAdmin.find(
          (org: Organization) => org.id === mapping.organizationId
        );

        return organization || null;
      } catch (error) {
        console.warn('Sample organization data not available:', error);
        return null;
      }
    });
  }

  /**
   * Check if a user belongs to a specific organization
   */
  async userBelongsToOrganization(userUid: string, organizationId: string): Promise<boolean> {
    if (!isSampleDataMode() || !isSampleDataUser(userUid)) {
      const user = await this.getUserById(userUid);
      return user?.organizationId === organizationId;
    }

    const mapping = getUserOrganizationMapping(userUid);
    return mapping?.organizationId === organizationId;
  }

  /**
   * Generate display name from user data
   */
  private generateDisplayName(userData: Partial<User>, existingUser?: User): string {
    const firstName = userData.firstName || existingUser?.firstName || '';
    const lastName = userData.lastName || existingUser?.lastName || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    
    return userData.email || existingUser?.email || 'Unknown User';
  }

  /**
   * Update demo user helper
   */
  private async updateDemoUser(userId: string, updatedUser: User): Promise<User> {
    const index = this.demoUsers.findIndex(user => user.uid === userId);
    if (index !== -1) {
      this.demoUsers[index] = updatedUser;
    }
    return JSON.parse(JSON.stringify(updatedUser));
  }

  /**
   * Get user account settings
   */
  async getUserAccountSettings(userId: string): Promise<UserAccountSettings> {
    console.log(`UserManager: getUserAccountSettings called for userId: ${userId}`);
    
    // Check if we have settings in memory
    if (this.userSettingsStore[userId]) {
      console.log(`UserManager: Found settings in memory for userId: ${userId}`);
      return this.userSettingsStore[userId];
    }

    // Return default settings for new users
    const defaultSettings: UserAccountSettings = {
      ...this.defaultSettingsForNewUser,
      userUid: userId,
    };

    this.userSettingsStore[userId] = defaultSettings;
    console.log(`UserManager: Created default settings for userId: ${userId}`);
    return defaultSettings;
  }

  /**
   * Update user account settings
   */
  async updateUserAccountSettings(userId: string, updates: Partial<UserAccountSettings>): Promise<UserAccountSettings | null> {
    console.log(`UserManager: updateUserAccountSettings called for userId: ${userId} with`, updates);
    
    const currentSettings = await this.getUserAccountSettings(userId);
    const updatedSettings = { ...currentSettings, ...updates };
    
    this.userSettingsStore[userId] = updatedSettings;
    console.log(`UserManager: Updated settings for userId: ${userId}`);
    
    return updatedSettings;
  }

  /**
   * Check if user exists
   */
  async userExists(userId: string): Promise<boolean> {
    const user = await this.getUserById(userId);
    return user !== null;
  }

  /**
   * Get user count
   */
  async getUserCount(): Promise<number> {
    const users = await this.getAllUsers();
    return users.length;
  }

  /**
   * Get users by status
   */
  async getUsersByStatus(status: UserStatusType): Promise<User[]> {
    return this.withDelay(async () => {
      const allUsers = await this.getAllUsers();
      return allUsers.filter(user => user.status === status);
    });
  }

  /**
   * Search users
   */
  async searchUsers(searchTerm: string): Promise<User[]> {
    return this.withDelay(async () => {
      const allUsers = await this.getAllUsers();
      
      return new FilterBuilder(allUsers)
        .search(searchTerm, ['displayName', 'email', 'firstName', 'lastName'])
        .build();
    });
  }
}

export const userManager = new UserManager(); 
